import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Role } from '@/types';
import { Shield } from 'lucide-react';
import { useRoles } from '@/store/role';
import { useLayout } from '@/contexts/layout-context';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/loader';
import { ComposedTable, AvatarCell, StatusCell, BadgeCell, BadgeListCell, ActionCell, type FilterDef, type FilterValues } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

const FILTERS: FilterDef[] = [
  {
    type: 'chips',
    key: 'scopes',
    label: 'Scope',
    options: ['Platform', 'Tenant', 'TenantApplication']
  },
  {
    type: 'radio',
    key: 'roleType',
    label: 'Type',
    options: ['All', 'Built-in', 'Custom']
  },
  {
    type: 'radio',
    key: 'status',
    label: 'Status',
    options: ['All', 'Active', 'Deprecated']
  },
];

export const RolesPage = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const listRoles = useRoles((state) => state.list);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  useEffect(() => {
    setBreadcrumbs([{ name: 'Roles & Permissions' }, { name: 'Roles' }]);
    listRoles()
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch((e) => console.error('Failed to load roles', e))
      .finally(() => setLoading(false));
  }, [listRoles, setBreadcrumbs]);

  const columns = [
    {
      id: 'name',
      header: 'Role Name',
      accessorKey: 'name',
      cell: ({ row }: any) => {
        const r = row.original as Role;
        const idx = roles.indexOf(r);
        return (
          <AvatarCell
            name={r.name}
            subtitle={r.description || 'No description'}
            index={idx}
            onClick={() => navigate(`/m/roles/${r.id}`)}
          />
        );
      },
    },
    {
      id: 'scopes',
      header: 'Applicable Scopes',
      cell: ({ row }: any) => {
        const r = row.original as Role;
        return <BadgeListCell items={r.applicable_scopes || []} />;
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }: any) => {
        const r = row.original as Role;
        const isSystemManaged = r.is_system_managed;
        return (
          <BadgeCell
            label={isSystemManaged ? 'Built-in' : 'Custom'}
            variant={isSystemManaged ? 'purple' : 'default'}
          />
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }: any) => {
        const r = row.original as Role;
        return <StatusCell status={r.deprecated ? 'Inactive' : 'Active'} />;
      },
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }: any) => {
        const r = row.original as Role;
        return (
          <span className="text-[13px] text-slate-600 dark:text-slate-400">
            {r.category || 'General'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: any) => <ActionCell onClick={() => navigate(`/m/roles/${row.original.id}`)} />,
    },
  ];

  const filterFn = (row: Role, values: FilterValues) => {
    // Filter by scopes
    const scopes = values['scopes'] as string[];
    if (scopes?.length > 0) {
      const hasMatchingScope = row.applicable_scopes?.some(scope => scopes.includes(scope));
      if (!hasMatchingScope) return false;
    }

    // Filter by role type
    const roleType = values['roleType'] as string;
    if (roleType && roleType !== 'All') {
      const isSystemManaged = row.is_system_managed;
      if (roleType === 'Built-in' && !isSystemManaged) return false;
      if (roleType === 'Custom' && isSystemManaged) return false;
    }

    // Filter by status
    const status = values['status'] as string;
    if (status && status !== 'All') {
      if (status === 'Active' && row.deprecated) return false;
      if (status === 'Deprecated' && !row.deprecated) return false;
    }

    return true;
  };

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <Shield className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
        No roles found
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Get started by creating your first custom role
      </p>
      <Button onClick={() => navigate('/m/roles/create')}>
        Create Role
      </Button>
    </div>
  );

  return (
    <PageLayout title="Roles" description="Define role policies and group associated permission mappings.">
      {loading ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
          <Loader />
        </div>
      ) : (
        <ComposedTable
          data={roles}
          columns={columns}
          filters={FILTERS}
          filterFn={filterFn}
          searchPlaceholder="Search roles..."
          emptyState={emptyState}
          onAdd={() => navigate('/m/roles/create')}
          itemLabel="roles"
        />
      )}
    </PageLayout>
  );
};
